import React from 'react'
import UserLayout from '../../Layouts/UserLayout'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { motion } from 'framer-motion'
import useTranslation from '../../hooks/useTranslation'

export default function Index({ stats, recent_sessions, heatmap_data, penalty_trend }) {
  const { t } = useTranslation()

  return (
    <UserLayout>
      <div className="space-y-8">
        
        {/* Welcome Section */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('dashboard.title')}</h1>
          <p className="text-gray-600 mt-2">{t('dashboard.subtitle')}</p>
        </div>

        {/* Top Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard title={t('dashboard.total_xp')} value={stats.total_xp} color="emerald" icon="⚡️" />
          <StatCard title={t('dashboard.texts_read')} value={stats.total_texts} color="blue" icon="📚" />
          <StatCard title={t('dashboard.current_streak')} value={`${stats.streak} ${t('dashboard.days')}`} color="amber" icon="🔥" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Main Column (Charts) */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* Heatmap (Activity) */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
               <h3 className="text-lg font-semibold mb-6">{t('dashboard.activity')}</h3>
               <div className="grid grid-cols-10 gap-2">
                  {heatmap_data.map((day, i) => (
                    <div key={i} className="flex flex-col items-center gap-1 group relative">
                      <div 
                        className={`w-full pt-[100%] rounded-md transition-colors ${getColorForCount(day.count)}`} 
                      ></div>
                      {/* Tooltip on hover */}
                      <div className="absolute bottom-full mb-2 hidden group-hover:block z-10 bg-black text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                        {day.date}: {day.count} texts
                      </div>
                    </div>
                  ))}
               </div>
            </div>

            {/* Line Chart (Penalty Trend) */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 h-80">
               <h3 className="text-lg font-semibold mb-4">{t('dashboard.click_ratio')}</h3>
               {penalty_trend.length > 1 ? (
                 <ResponsiveContainer width="100%" height="100%">
                   <LineChart data={penalty_trend}>
                     <CartesianGrid strokeDasharray="3 3" vertical={false} />
                     <XAxis dataKey="date" tick={{fontSize: 12}} />
                     <YAxis tick={{fontSize: 12}} />
                     <Tooltip 
                       contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                     />
                     <Line 
                       type="monotone" 
                       dataKey="ratio" 
                       stroke="#10b981" 
                       strokeWidth={3}
                       activeDot={{ r: 8 }} 
                     />
                   </LineChart>
                 </ResponsiveContainer>
               ) : (
                 <div className="h-full flex items-center justify-center text-gray-500">
                   {t('dashboard.not_enough_data')}
                 </div>
               )}
            </div>

          </div>

          {/* Sidebar Column (History) */}
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
               <h3 className="text-lg font-semibold mb-4">{t('dashboard.recent_activity')}</h3>
               <div className="space-y-4">
                 {recent_sessions.length === 0 && <p className="text-gray-500 text-sm">{t('dashboard.no_activity')}</p>}
                 {recent_sessions.map(session => (
                   <div key={session.id} className="flex items-start gap-3 pb-4 border-b last:border-0 last:pb-0">
                     <div className="bg-emerald-100 text-emerald-600 p-2 rounded-lg text-xl">
                       📖
                     </div>
                     <div>
                       <div className="font-medium text-gray-900">{session.text.title}</div>
                       <div className="text-xs text-gray-500 mt-1">
                         {new Date(session.completed_at).toLocaleDateString()} • {session.penalty_count} {t('dashboard.clicks')}
                       </div>
                     </div>
                     <div className="ml-auto font-bold text-gray-400 text-sm">
                       +{session.text.difficulty * 10} XP
                     </div>
                   </div>
                 ))}
               </div>
            </div>
          </div>

        </div>
      </div>
    </UserLayout>
  )
}

function StatCard({ title, value, color, icon }) {
  const colors = {
    emerald: 'bg-emerald-50 text-emerald-700',
    blue: 'bg-blue-50 text-blue-700',
    amber: 'bg-amber-50 text-amber-700',
  }

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${colors[color]}`}>
        {icon}
      </div>
      <div>
        <div className="text-sm font-medium text-gray-500">{title}</div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
      </div>
    </div>
  )
}

function getColorForCount(count) {
  if (count === 0) return 'bg-gray-100'
  if (count === 1) return 'bg-emerald-200'
  if (count === 2) return 'bg-emerald-300'
  if (count === 3) return 'bg-emerald-400'
  return 'bg-emerald-500'
}

